/**
 * @file CouponsTable.tsx
 *
 * List-view table for coupons: code, discount type, amount, usage, expiry, an
 * active/expired status pill ({@link StatusBadge}), and per-row edit/delete
 * actions. Which columns render is decided by the parent (responsive flags) and
 * passed in. Presentational: data, selection, and persistence stay in
 * {@link CouponsView}.
 */

import { Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { StatusBadge } from '../components/StatusBadge';
import type { ShopCoupon } from '../api/shop-types';
import {
  discountTypeLabel,
  formatAmount,
  formatExpiry,
  formatUsage,
} from './coupon-list';
import { couponStatus, statusIndicator, statusLabel } from './coupon-status';

interface CouponsTableProps {
  coupons: ShopCoupon[];
  currencySymbol: string;
  showType: boolean;
  showUsage: boolean;
  showExpiry: boolean;
  onEdit: (coupon: ShopCoupon) => void;
  onDelete: (coupon: ShopCoupon) => void;
}

/**
 * Renders the coupons list table.
 *
 * @param props - The coupons, currency symbol, responsive column flags, and the
 *   row edit/delete handlers.
 *
 * @returns The table element.
 */
export function CouponsTable({
  coupons,
  currencySymbol,
  showType,
  showUsage,
  showExpiry,
  onEdit,
  onDelete,
}: CouponsTableProps) {
  const intl = useIntl();

  return (
    <table data-testid="coupons-table">
      <thead>
        <tr>
          <th scope="col">
            <FormattedMessage {...adminMessages.couponsColCode} />
          </th>
          {showType && (
            <th scope="col">
              <FormattedMessage {...adminMessages.couponsColType} />
            </th>
          )}
          <th scope="col">
            <FormattedMessage {...adminMessages.couponsColAmount} />
          </th>
          {showUsage && (
            <th scope="col">
              <FormattedMessage {...adminMessages.couponsColUsage} />
            </th>
          )}
          {showExpiry && (
            <th scope="col">
              <FormattedMessage {...adminMessages.couponsColExpires} />
            </th>
          )}
          <th scope="col">
            <FormattedMessage {...adminMessages.couponsColStatus} />
          </th>
          <th scope="col" aria-hidden="true" />
        </tr>
      </thead>
      <tbody>
        {coupons.map((coupon) => {
          const typeDesc = discountTypeLabel(coupon.discount_type);
          const status = couponStatus(coupon);
          return (
            <tr key={coupon.id} data-testid="coupon-row" data-id={coupon.id}>
              <td>
                <span className="gv-text-sm gv-text-bold">{coupon.code}</span>
              </td>
              {showType && (
                <td>
                  <span className="gv-text-sm">
                    {typeDesc ? intl.formatMessage(typeDesc) : coupon.discount_type}
                  </span>
                </td>
              )}
              <td>
                <span className="gv-text-sm">{formatAmount(coupon, currencySymbol)}</span>
              </td>
              {showUsage && (
                <td>
                  <span className="gv-text-sm">{formatUsage(coupon)}</span>
                </td>
              )}
              {showExpiry && (
                <td>
                  <span className="gv-text-sm">{formatExpiry(coupon.date_expires)}</span>
                </td>
              )}
              <td>
                <StatusBadge
                  indicator={statusIndicator(status)}
                  label={intl.formatMessage(statusLabel(status))}
                />
              </td>
              <td>
                <div className="gv-flex gv-justify-end gv-gap-xs">
                  <button
                    className="gv-button gv-button-icon"
                    type="button"
                    data-testid="coupon-row-edit"
                    aria-label={intl.formatMessage(adminMessages.couponsEdit)}
                    onClick={() => onEdit(coupon)}
                  >
                    <Icon name="edit" size="small" />
                  </button>
                  <button
                    className="gv-button gv-button-icon"
                    type="button"
                    data-testid="coupon-row-delete"
                    aria-label={intl.formatMessage(adminMessages.couponsDelete)}
                    onClick={() => onDelete(coupon)}
                  >
                    <Icon name="delete" size="small" />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
